import { HTMLAttributes } from 'react'
import Link from 'next/link'

import Button from '@/components/base/Button'
import BuyButtonSDK from '@/components/base/BuyButtonSDK'
import Container from '@/components/base/Container'
import Grid from '@/components/base/Grid'
import Image from '@/components/base/Image'
import Motion from '@/components/base/Motion'
import Typography from '@/components/base/Typography'
import { useStore } from '@/contexts/store'

import { imageParagraph } from './animations'
import { cn } from '@/lib/utils'
import { WPImage, WPLink, WPPageData } from '@/types'

export type ProductParagraphProps = HTMLAttributes<HTMLElement> &
  Partial<{
    item: {
      featured_image: WPImage
      link: string
      title: {
        rendered: string
      }
      acf: {
        paragraph: {
          label: string
          sub_title: string
          text: string
        }
        ecommerce: {
          shop_online_link: WPLink
          use_shopify_prod_id: boolean
          shopify_prod_id: string
        }
      }
    }[]
    alignment: 'left' | 'right'
    theme: WPPageData['acf']['theme']
  }>

const ProductParagraph = ({
  alignment = 'left',
  item,
  theme = 'light',
  ...props
}: ProductParagraphProps) => {
  const { strings } = useStore()

  const shopifyProductId = item?.[0]?.acf.ecommerce.shopify_prod_id // '7903687245912'
  const shopifyProductNode = shopifyProductId
    ? `${strings?.client_data?.selettore_btn || 'shopify-component'}-${shopifyProductId}`
    : undefined

  return item && item.length > 0 ? (
    <>
      {item[0]?.acf.ecommerce.use_shopify_prod_id && shopifyProductId ? (
        <BuyButtonSDK id={shopifyProductId} node={shopifyProductNode || ''} />
      ) : null}
      <Motion animation={imageParagraph}>
        {(motionRef) => (
          <section
            ref={motionRef}
            {...props}
            className={cn([
              {
                'bg-dark-100 text-dark-500': theme === 'light',
                'bg-dark-500 text-white': theme === 'dark'
              }
            ])}
          >
            <Container>
              <Grid className="gap-y-gutter">
                <div
                  className={cn([
                    'col-span-4 flex flex-col justify-center gap-[1.6rem]',
                    'md:col-span-6',
                    'lg:col-span-5 lg:gap-[3.2rem]',
                    {
                      'lg:col-start-2': alignment === 'left',
                      'lg:col-start-7': alignment === 'right'
                    }
                  ])}
                >
                  {item[0].title.rendered ? (
                    <Typography variant="h2" data-motion-title>
                      {item[0].title.rendered}
                    </Typography>
                  ) : null}
                  {item[0]?.acf.paragraph.label ? (
                    <Typography
                      variant="label"
                      className="font-extrabold"
                      data-motion-label
                    >
                      {item[0].acf.paragraph.label}
                    </Typography>
                  ) : null}
                  {item[0]?.acf.paragraph.sub_title ? (
                    <Typography variant="h3" data-motion-subtitle>
                      {item[0].acf.paragraph.sub_title}
                    </Typography>
                  ) : null}
                  {item[0]?.acf.paragraph.text ? (
                    <Typography variant="body" data-motion-paragraph>
                      {item[0].acf.paragraph.text}
                    </Typography>
                  ) : null}
                  <div
                    className="relative z-10 flex gap-[0.5rem]"
                    data-motion-cta
                  >
                    {item[0]?.acf.ecommerce.use_shopify_prod_id &&
                    shopifyProductId &&
                    shopifyProductNode ? (
                      <div id={shopifyProductNode} />
                    ) : (
                      <Button type="button" variant={theme}>
                        <Link
                          href={item[0].acf.ecommerce.shop_online_link.url}
                          data-label={
                            item[0].acf.ecommerce.shop_online_link.title
                          }
                          scroll={false}
                        >
                          <Typography variant="label">
                            {item[0].acf.ecommerce.shop_online_link.title}
                          </Typography>
                        </Link>
                      </Button>
                    )}
                  </div>
                </div>
                <div
                  className={cn([
                    'col-span-4',
                    'md:col-span-6',
                    'lg:col-span-4',
                    {
                      'lg:col-start-8': alignment === 'left',
                      '-order-1 lg:col-start-2': alignment === 'right'
                    }
                  ])}
                >
                  <div className="aspect-h-5 aspect-w-4">
                    <Image
                      source={item[0].featured_image}
                      size="large"
                      data-motion-image
                    />
                  </div>
                </div>
              </Grid>
            </Container>
          </section>
        )}
      </Motion>
    </>
  ) : null
}

export default ProductParagraph
